import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { LoadingState } from "@repo/app-types/client/client";
import type { Teammate } from "@repo/app-types/entities/teammate";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import type { RootState } from "../../app/store.js";

const teammatesAdapter = createEntityAdapter<Teammate>();

const initialState = teammatesAdapter.getInitialState<{
  status: LoadingState;
  error: string | null;
}>({
  status: LoadingState.LOADED,
  error: null,
});

export const fetchTeammates = createAsyncThunk(
  "teammates/fetchAll",
  async (_parameters, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.get("/teammates");

      return thunkAPI.fulfillWithValue(response.data.teammates);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const teammatesSlice = createSlice({
  name: "teammates",
  initialState,
  reducers: {
    teammateAdded: teammatesAdapter.addOne,
    teammateRemoved: teammatesAdapter.removeOne,
    teammateUpdated: teammatesAdapter.updateOne,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeammates.pending, (state) => {
        state.status = LoadingState.LOADING;
      })
      .addCase(fetchTeammates.fulfilled, (state, action) => {
        state.status = LoadingState.LOADED;
        teammatesAdapter.setAll(state, action.payload);
      })
      .addCase(fetchTeammates.rejected, (state, action) => {
        state.status = LoadingState.ERROR;
        state.error = action.error.message || null;
      });
  },
});

export const { teammateAdded, teammateRemoved, teammateUpdated } =
  teammatesSlice.actions;

export const selectAllTeammates = teammatesAdapter.getSelectors().selectAll;
export const selectTeammatesLoadingState = (state: RootState) =>
  state.teammates.status;

export default teammatesSlice.reducer;
